import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, Shield, Globe, TrendingUp, Zap, Brain, BarChart3, Users, Building2, Truck, Store, Gift, Smartphone, Target, Award, Layers } from 'lucide-react';
import { DemoModal } from '@/components/DemoModal';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';
import TrendingSolutions from '@/components/TrendingSolutions';
import TestimonialsLaunches from '@/components/sections/TestimonialsLaunches';
import HeroBannerCarousel from '@/components/HeroBannerCarousel';

// Hero — full-width banner carousel
const HeroSection = () => (
  <section className="hero-banner-full" data-testid="hero-section">
    <HeroBannerCarousel />
  </section>
);

// Metrics Strip
const MetricsStrip = () => (
  <div className="bg-[#2C39B8] py-5 halftone-overlay" data-testid="metrics-strip">
    <div className="container">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-8 text-center">
        {[
          { val: '1M+', label: 'Channel Partners', green: false },
          { val: '$5M+', label: 'Rewards Distributed', green: true },
          { val: '50+', label: 'Enterprise Programs', green: false },
          { val: '12', label: 'Industries Served', green: true },
          { val: '92%', label: 'Redemption Rate', green: false },
        ].map(function(item, idx) {
          return (
            <div key={item.label} className={idx === 4 ? 'col-span-2 md:col-span-1' : ''}>
              <div className={'text-2xl md:text-3xl font-bold ' + (item.green ? 'text-[#7CC326]' : 'text-white')}>{item.val}</div>
              <div className="text-xs text-slate-300 mt-1">{item.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

// Stakeholder Section
const StakeholderSection = ({ onDemoClick }) => (
  <section className="py-20 bg-white halftone-corner" data-testid="stakeholder-section">
    <div className="container">
      <div className="grid md:grid-cols-2 gap-16 items-center">
        <div>
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase border border-[#7CC326]/30 text-[#7CC326] bg-[#7CC326]/5 mb-5">One Platform</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#2C39B8] mb-4 leading-tight">
            Every Stakeholder.<br />One Ecosystem.
          </h2>
          <p className="text-gray-500 mb-8 text-base leading-relaxed">
            Align partners, track performance, and scale loyalty programs that drive real business outcomes across your entire channel.
          </p>
          <div className="flex gap-3 flex-wrap">
            <button onClick={onDemoClick} className="btn btn-primary" data-testid="stakeholder-demo-btn">
              Explore Platform <ArrowRight size={16} />
            </button>
            <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-semibold text-[#2C39B8] hover:text-[#7CC326] transition-colors px-4 py-3">
              View Use Cases <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[
            { icon: Building2, title: 'Manufacturers', desc: 'Drive engagement and unlock growth across distribution.', color: '#2C39B8' },
            { icon: Users, title: 'Distributors', desc: 'Gain real-time visibility and actionable insights.', color: '#6FC8F5' },
            { icon: Store, title: 'Dealers & Retailers', desc: 'Earn rewards that matter and keep coming back.', color: '#7CC326' },
            { icon: Target, title: 'Influencers', desc: 'Painters, contractors, architects — rewarded for advocacy.', color: '#5EA31C' },
          ].map(function(item) {
            var Icon = item.icon;
            return (
              <div key={item.title} className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-lg transition-all group" style={{borderTopColor: item.color, borderTopWidth: '3px'}}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{background: item.color + '12', border: '2px solid ' + item.color + '30'}}>
                  <Icon size={22} style={{color: item.color}} />
                </div>
                <h4 className="font-bold text-[#2C39B8] text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

// AI & Insights Section
const InsightsSection = () => (
  <section className="py-20 bg-gray-50 halftone-overlay-dark" data-testid="insights-section">
    <div className="container">
      <div className="grid md:grid-cols-2 gap-16 items-center">
        <div>
          <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase border border-[#2C39B8]/15 text-[#2C39B8] bg-[#2C39B8]/5 mb-5">AI-Powered</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#2C39B8] mb-4 leading-tight">
            Insights That<br /><span className="text-[#7CC326]">Power Decisions.</span>
          </h2>
          <p className="text-gray-500 mb-8 text-base leading-relaxed">
            Real-time dashboards and reports to help you optimize, incentivize, and outperform.
          </p>
          <div className="space-y-5">
            {[
              { icon: BarChart3, title: 'Real-time Performance', desc: 'Track performance across programs, partners, and regions.' },
              { icon: Brain, title: 'Actionable Insights', desc: 'Identify trends, measure impact, and act with confidence.' },
              { icon: Target, title: 'Custom Reports', desc: 'Tailored reports for every stakeholder and business need.' },
            ].map(function(item) {
              var Icon = item.icon;
              return (
                <div key={item.title} className="flex gap-4 items-start">
                  <div className="w-11 h-11 rounded-full border-2 border-[#7CC326]/30 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#2C39B8]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#2C39B8] text-sm">{item.title}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex gap-3 flex-wrap">
            <Link to="/ai-brain" className="btn btn-primary" data-testid="insights-cta">
              See Dashboards <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-semibold text-[#2C39B8] hover:text-[#7CC326] transition-colors px-4 py-3">
              Schedule a Demo <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
          <div className="grid grid-cols-2 gap-4 mb-5">
            {[
              { val: '4,521', label: 'Active Partners', color: '#2C39B8' },
              { val: '92.5%', label: 'Redemption Rate', color: '#7CC326' },
              { val: '3.2x', label: 'Campaign ROI', color: '#2C39B8' },
              { val: '-45%', label: 'Churn Reduction', color: '#7CC326' },
            ].map(function(item) {
              return (
                <div key={item.label} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="text-2xl font-bold" style={{color: item.color}}>{item.val}</div>
                  <div className="text-xs text-gray-400 mt-1">{item.label}</div>
                </div>
              );
            })}
          </div>
          <div className="bg-[#2C39B8] rounded-xl p-4 text-center">
            <div className="text-xs text-slate-300 mb-1">AI Query</div>
            <div className="text-sm text-white font-medium">"Show me top 10 dormant dealers in Maharashtra"</div>
            <div className="text-[10px] text-[#7CC326] mt-2 font-semibold">Instant answer from natural language query</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Channel Roles — with imagery and better visual treatment
const ChannelRolesSection = () => (
  <section className="py-20 bg-white halftone-corner" data-testid="channel-roles-section">
    <div className="container">
      <div className="text-center mb-12">
        <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase border border-[#7CC326]/30 text-[#7CC326] bg-[#7CC326]/5 mb-4">Loyalty Programs</span>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Built for Every Layer of Your Channel</h2>
        <p className="text-gray-500 max-w-xl mx-auto">Purpose-built loyalty programs for manufacturers, distributors, dealers, and influencers — each with unique incentive logic.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { icon: Building2, title: 'Primary Sales', desc: 'Manufacturer to distributor — purchase-linked incentives, tier-based rewards, dispatch tracking.', path: '/solutions?stakeholder=Manufacturer', gradient: 'from-[#2C39B8] to-[#6FC8F5]' },
          { icon: Truck, title: 'Secondary Sales', desc: 'Distributor to retailer — QR scan-and-earn, invoice verification, instant UPI payouts.', path: '/solutions?stakeholder=Distributor', gradient: 'from-[#6FC8F5] to-[#2C39B8]' },
          { icon: Store, title: 'Dealer & Retailer', desc: 'POS integration, leaderboards, volume-based rewards, WhatsApp-first engagement.', path: '/solutions?stakeholder=Dealer', gradient: 'from-[#7CC326] to-[#5EA31C]' },
          { icon: Users, title: 'Influencer & Tertiary', desc: 'Painters, electricians, architects — scan-and-earn, instant UPI, LoyltYKart rewards.', path: '/solutions?stakeholder=Influencer', gradient: 'from-[#5EA31C] to-[#7CC326]' },
          { icon: Brain, title: 'AI Attribution', desc: 'Influence-to-sale tracking, churn prediction, natural language dashboards, campaign optimization.', path: '/ai-brain', gradient: 'from-[#2C39B8] to-[#1F2C99]' },
          { icon: Smartphone, title: 'WhatsApp Loyalty', desc: 'Full loyalty bot — onboarding, scanning, rewards, catalog, videos. 85%+ adoption rate.', path: '/platform', gradient: 'from-[#2C39B8] to-[#6FC8F5]' },
        ].map(function(item) {
          var Icon = item.icon;
          return (
            <Link key={item.title} to={item.path} className="group block rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all" style={{textDecoration: 'none', color: 'inherit'}} data-testid={'role-card-' + item.title.toLowerCase().replace(/\s/g, '-')}>
              <div className={'bg-gradient-to-br ' + item.gradient + ' p-6 flex items-center gap-4'}>
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Icon size={24} className="text-white" />
                </div>
                <h4 className="text-white font-bold text-lg">{item.title}</h4>
              </div>
              <div className="p-5 bg-white">
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                <span className="text-xs font-semibold text-[#7CC326] mt-3 block group-hover:translate-x-1 transition-transform">Explore solutions &rarr;</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

// Platform Architecture — Redesigned as a visual hub
const PlatformSection = () => (
  <section className="py-20 bg-gray-50 halftone-overlay-dark" data-testid="platform-arch-section">
    <div className="container max-w-6xl">
      <div className="text-center mb-14">
        <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase border border-[#2C39B8]/15 text-[#2C39B8] bg-[#2C39B8]/5 mb-4">Platform</span>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">The 5-Band Architecture</h2>
        <p className="text-gray-500 max-w-2xl mx-auto">A complete operating system built in 5 integrated layers — each addressing a critical capability gap.</p>
      </div>

      {/* Visual Hub */}
      <div className="relative">
        {/* Center Hub */}
        <div className="flex justify-center mb-8">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-[#2C39B8] to-[#6FC8F5] flex items-center justify-center shadow-xl">
            <div className="text-center">
              <Layers size={28} className="text-white mx-auto mb-1" />
              <div className="text-white text-[10px] font-bold leading-tight">Channel<br/>Loyalty</div>
            </div>
          </div>
        </div>

        {/* 5 Bands */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            { num: '01', title: 'Channel Ecosystem', desc: 'Model real-world hierarchies — manufacturers, distributors, dealers, influencers.', icon: Building2, color: '#2C39B8', link: '/platform' },
            { num: '02', title: 'User Engagement', desc: 'QR scanning, WhatsApp bots, gamification, leaderboards, push notifications.', icon: Smartphone, color: '#6FC8F5', link: '/platform' },
            { num: '03', title: 'Rewards & Payout', desc: 'UPI, gift cards, LoyltYKart 270+ brands, insurance, travel, instant delivery.', icon: Gift, color: '#7CC326', link: '/rewards' },
            { num: '04', title: 'Platform Core', desc: 'No-code builder, multi-tenant, API-first, white-label, compliance & audit.', icon: Shield, color: '#5EA31C', link: '/platform' },
            { num: '05', title: 'AI Brain', desc: 'Churn prediction, NLP dashboards, campaign optimization, fraud detection.', icon: Brain, color: '#2C39B8', link: '/ai-brain' },
          ].map(function(band) {
            var Icon = band.icon;
            return (
              <Link key={band.num} to={band.link} className="group block" style={{textDecoration: 'none', color: 'inherit'}}>
                <div className="bg-white rounded-2xl p-5 border border-gray-100 hover:shadow-lg transition-all h-full relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1" style={{background: band.color}} />
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-bold text-white px-2 py-0.5 rounded-full" style={{background: band.color}}>{band.num}</span>
                    <Icon size={18} style={{color: band.color}} />
                  </div>
                  <h4 className="font-bold text-[#2C39B8] text-sm mb-2">{band.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{band.desc}</p>
                  <span className="text-[10px] font-semibold mt-3 block opacity-0 group-hover:opacity-100 transition-opacity" style={{color: band.color}}>Explore &rarr;</span>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Flow indicator */}
        <div className="flex justify-center items-center gap-3 mt-6">
          {['#2C39B8', '#6FC8F5', '#7CC326', '#5EA31C', '#2C39B8'].map(function(color, i) {
            return (
              <div key={i} className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full" style={{background: color}} />
                {i < 4 && <ArrowRight size={12} className="text-gray-300" />}
              </div>
            );
          })}
        </div>
        <p className="text-center text-xs text-gray-400 mt-3 font-medium">Align every layer. Reward every behavior. Measure every outcome.</p>
      </div>
    </div>
  </section>
);

// Why ChannelLoyalty
const WhySection = () => (
  <section className="py-20 bg-white" data-testid="why-channelloyalty-section">
    <div className="container">
      <div className="text-center mb-12">
        <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase border border-[#2C39B8]/15 text-[#2C39B8] bg-[#2C39B8]/5 mb-4">Why ChannelLoyalty</span>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Built by Loyalty Experts, for Enterprises</h2>
        <p className="text-gray-500 max-w-xl mx-auto">Decades of experience in designing and running loyalty at scale.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { icon: Shield, title: 'Enterprise Grade', desc: 'Designed and operated loyalty programs at massive scale across multiple geographies.' },
          { icon: Globe, title: 'Global Scale', desc: 'Multi-country, multi-tier program execution with local compliance built-in.' },
          { icon: Brain, title: 'AI-Native Platform', desc: 'Decision intelligence, not just dashboards. Ask questions, get answers.' },
          { icon: Award, title: 'Deep Rewards Catalog', desc: '270+ brands on LoyltYKart — Tanishq, Amazon, MakeMyTrip, Croma. Instant WhatsApp delivery.' },
          { icon: Smartphone, title: 'WhatsApp-First', desc: 'Full loyalty bot on WhatsApp — scan, earn, redeem. 85%+ adoption rate.' },
          { icon: Zap, title: 'Launch in Weeks', desc: 'No-code program builder. Simulate, launch, modify — deployed in 3 weeks, not months.' },
        ].map(function(item) {
          var Icon = item.icon;
          return (
            <div key={item.title} className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-[#7CC326]/30 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-full border-2 border-[#2C39B8]/15 flex items-center justify-center mb-4">
                <Icon size={22} className="text-[#2C39B8]" />
              </div>
              <h4 className="font-bold text-[#2C39B8] mb-2">{item.title}</h4>
              <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = ({ onDemoClick }) => (
  <section className="cta-section" data-testid="cta-section">
    <div className="container relative z-10">
      <h2>Ready to Transform Your Channel Loyalty?</h2>
      <p>See how ChannelLoyalty can align, incentivize, and optimize your entire B2B ecosystem.</p>
      <div className="cta-buttons">
        <button onClick={onDemoClick} className="btn btn-primary">
          Request Demo <ArrowRight size={18} />
        </button>
        <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
          <MessageCircle size={18} /> Talk to Expert
        </a>
      </div>
    </div>
  </section>
);

// People We Serve — Brand Handbook portraits
const PeopleSection = () => (
  <section className="py-20 bg-white" data-testid="people-section">
    <div className="container">
      <div className="text-center mb-12">
        <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase border border-[#2C39B8]/15 text-[#2C39B8] bg-[#2C39B8]/5 mb-4">Who We Serve</span>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Loyalty for Every Role in Your Channel</h2>
        <p className="text-gray-500 max-w-xl mx-auto">From factory floors to retail counters — we build reward programs for the people who drive your business.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
        {[
          { name: 'Construction Engineers', img: '/portraits/portrait_construction.png' },
          { name: 'Electricians', img: '/portraits/portrait_electrician.png' },
          { name: 'Plumbers', img: '/portraits/portrait_plumber.png' },
          { name: 'Carpenters', img: '/portraits/portrait_carpenter.png' },
          { name: 'Retailers', img: '/portraits/portrait_retailer.png' },
          { name: 'Dealers', img: '/portraits/portrait_dealer.png' },
        ].map(function(person) {
          return (
            <div key={person.name} className="text-center group">
              <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#6FC8F5]/10 via-[#B6E7F7]/10 to-[#B8E86F]/10 p-1">
                <img src={person.img} alt={person.name} className="w-full rounded-xl" style={{aspectRatio: '4/5', objectFit: 'cover'}} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Brand Gradient Strip
const GradientStrip = () => (
  <div className="h-2" style={{background: 'linear-gradient(90deg, #6FC8F5 0%, #B6E7F7 35%, #B8E86F 65%, #7CC326 100%)'}} />
);

// WhatsApp Float
const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float" data-testid="whatsapp-btn">
    <MessageCircle size={28} color="white" />
  </a>
);

// Main HomePage
export default function HomePage() {
  var demoState = useState(false);
  var demoModalOpen = demoState[0];
  var setDemoModalOpen = demoState[1];

  return (
    <div data-testid="home-page">
      <Navbar onDemoClick={function() { setDemoModalOpen(true); }} />
      <SEOHead title="B2B Channel Loyalty Platform for Dealers, Distributors & Retailers" description="ChannelLoyalty is India's #1 B2B channel loyalty platform. Design dealer rewards, distributor incentives, retailer engagement programs with AI analytics, instant UPI payouts, and 500+ reward brands." keywords="B2B loyalty platform, dealer loyalty program, distributor rewards, channel incentives, trade marketing platform India" canonical="https://channelloyalty.ai/" />
      
      <main>
        <HeroSection />
        <GradientStrip />
        <MetricsStrip />
        <StakeholderSection onDemoClick={function() { setDemoModalOpen(true); }} />
        <InsightsSection />
        <PeopleSection />
        <ChannelRolesSection />
        <TrendingSolutions />
        <PlatformSection />
        <TestimonialsLaunches />
        <WhySection />
        <CTASection onDemoClick={function() { setDemoModalOpen(true); }} />
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
      <DemoModal isOpen={demoModalOpen} onClose={function() { setDemoModalOpen(false); }} />
    </div>
  );
}
